import React, { useState } from 'react';
import { closeSession } from '../firebase/sparring.service';
import { WarningIcon, CelebrationIcon, LaunchIcon, ClipboardIcon, HourglassIcon } from '../../../icons';

interface SparringCloseDialogProps {
  sessionId: string;
  milestoneCount: number;
  onClose: () => void;
  onCancel: () => void;
}

const MIN_RECOMMENDED_MILESTONES = 3;

/**
 * SparringCloseDialog - Afsluitdialoog voor sparring sessies
 * 
 * - Check: >= 3 milestones aanbevolen
 * - < 3 = waarschuwing, niet blokkeren
 * - Markeer sessie als closed
 */
const SparringCloseDialog: React.FC<SparringCloseDialogProps> = ({
  sessionId,
  milestoneCount,
  onClose,
  onCancel,
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [showWarning, setShowWarning] = useState(milestoneCount < MIN_RECOMMENDED_MILESTONES);

  const handleClose = async () => {
    setIsClosing(true);
    try {
      await closeSession(sessionId);
      
      // Dispatch event to notify AgendaPage to switch to Doelstellingen tab
      window.dispatchEvent(new CustomEvent('sparringSessionClosed'));
      
      onClose();
    } catch (err) {
      console.error('Failed to close session:', err);
      setIsClosing(false);
    }
  };

  const handleConfirmWithWarning = () => {
    setShowWarning(false);
  };

  // Show warning if < 3 milestones
  if (showWarning) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="bg-white border-2 border-yellow-200 rounded-xl p-6 shadow-2xl max-w-md w-full">
          <div className="flex items-start gap-3 mb-4">
            <WarningIcon size={24} />
            <div>
              <h4 className="font-medium text-gray-900">
                Weinig mijlpalen vastgelegd
              </h4>
              <p className="text-sm text-gray-600 mt-1">
                Je hebt {milestoneCount} {milestoneCount === 1 ? 'mijlpaal' : 'mijlpalen'} vastgelegd. 
                We raden aan om minimaal {MIN_RECOMMENDED_MILESTONES} herkenningspunten te hebben.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
            >
              Terug naar sessie
            </button>
            <button
              onClick={handleConfirmWithWarning}
              className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors font-medium"
            >
              Toch afsluiten
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Success confirmation with clear next step
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
      <div 
        className="bg-gradient-to-br from-green-50 to-blue-50 border-2 border-green-200 rounded-xl p-8 shadow-2xl max-w-lg w-full"
        style={{ animation: 'slideUp 0.3s ease-out' }}
      >
      {/* Success header */}
      <div className="text-center mb-6">
        <div className="inline-block mb-3">
          <CelebrationIcon size={48} />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Perfect! Alles is Opgeslagen
        </h2>
        <p className="text-lg text-gray-700">
          {milestoneCount} {milestoneCount === 1 ? 'mijlpaal en actie' : 'mijlpalen en acties'} klaarstaan voor je
        </p>
      </div>

      {/* What's next card */}
      <div className="bg-white rounded-lg p-6 mb-6 border border-green-200">
        <div className="flex items-center gap-3 mb-3">
          <LaunchIcon size={24} />
          <h3 className="text-lg font-semibold text-gray-900">
            Wat is de volgende stap?
          </h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Voor elke mijlpaal is automatisch een actie aangemaakt. Je kunt nu direct aan de slag!
        </p>
        <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-r">
          <p className="font-medium text-orange-900 text-base mb-2">
            <ClipboardIcon size={16} className="inline" /> Je Acties Staan Klaar!
          </p>
          <ul className="text-sm text-orange-700 space-y-1 list-disc list-inside">
            <li>Bekijk je mijlpalen en acties in de tijdlijn</li>
            <li>Voer acties uit en vink ze af</li>
            <li>Volg je voortgang per periode</li>
          </ul>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={onCancel}
          disabled={isClosing}
          className="px-6 py-3 text-gray-600 hover:text-gray-800 disabled:opacity-50 font-medium"
        >
          ← Terug
        </button>
        <button
          onClick={handleClose}
          disabled={isClosing}
          className="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg text-lg font-bold hover:from-green-600 hover:to-green-700 disabled:bg-gray-300 transition-all shadow-md hover:shadow-lg disabled:shadow-none"
        >
          {isClosing ? <><HourglassIcon size={16} className="inline" /> Afsluiten...</> : <><LaunchIcon size={16} className="inline" /> Bekijk Mijn Acties →</>}
        </button>
      </div>
      </div>
    </div>
  );
};

export default SparringCloseDialog;
